import React, { useEffect, useState } from "react";
import classes from "./MovieDetail.module.css";
import { useParams } from "react-router-dom";
import Navbar from "../Components/Navbar";
import BackgroundMovieLayout from "../Components/BackgroundMovieLayout";
import {
  useGetMovieDetailsQuery,
  useGetReviewsQuery,
  useGetWatchProvidersMutation,
} from "../Slices/httpSlice";
import { useAnimationControls } from "framer-motion";
import ReviewModal from "../Components/ReviewModal";
import { useAppDispatch, useAppSelector } from "../Store/hooks";
import { toggleReviewModal } from "../Slices/reviewModalSlice";
import BottomNavbar from "../Components/BottomNavbar";
import NavbarMobile2 from "../Components/NavbarMobile2";
import SkeletonCardsGrid from "../Components/SkeletonCardsGrid";
import MediaDetails from "../Components/MediaDetails/MediaDetails";
import MediaSubTitle from "../Components/MediaDetails/MediaSubTitle";
import CardVideoPlayer from "../Components/MediaDetails/CardVideoPlayer";
import MediaImages from "../Components/MediaDetails/MediaImages";
import MediaCast from "../Components/MediaDetails/MediaCast";
import MediaReviews from "../Components/MediaDetails/MediaReviews";
import MediaRecommendations from "../Components/MediaDetails/MediaRecommendations";
import AuthModal from "../Components/Modals/AuthModal";
import ViewReviewModal from "../Components/ViewReviewModal";
import { openViewReviewModal } from "../Slices/viewReviewModalSlice";
import { toast } from "react-toastify";
import { setAuthModal } from "../Slices/authModalSlice";
import SearchModal from "../Components/SearchModal";
import WatchlistModal from "../Components/WatchlistModal";
import UserRatingsModal from "../Components/UserRatingsModal";
import ProfileModal from "../Components/ProfileModal";
import Drawer from "../Components/Drawer";
import MediaDetailsLoading from "../Components/MediaDetails/MediaDetailsLoading";
import MediaSubTitleForStreamingPlatforms from "../Components/MediaDetails/MediaSubTitleForStreamingPlatforms";
import { toggleWatchProvidersModal } from "../Slices/watchProvidersModalSlice";
import { motion } from "framer-motion";

const MovieDetail = () => {
  const { movieId } = useParams();

  const [windowWidth, setWindowWidth] = useState<number>(0);

  const { user } = useAppSelector((state) => state.userSlice);

  const [getWatchProvidersApi, { isLoading: watchProvidersLoading }] =
    useGetWatchProvidersMutation();

  const [watchProviders, setWatchProviders] = useState<any>({});
  const [watchProvidersLength, setWatchProvidersLength] = useState(0);
  const [wpLoading, setWpLoading] = useState(true);

  const fetchWatchProviders = async () => {
    setWpLoading(true);
    const response: any = await getWatchProvidersApi({
      id: movieId,
      media_type: "movie",
    });
    setWpLoading(false);
    if (response?.error) {
      toast.error(
        response?.error?.data?.error?.error ||
          response?.error?.data?.error ||
          response?.error?.data ||
          response?.error?.message ||
          "Something Went Wrong!"
      );
    } else {
      setWatchProviders(response?.data?.result);
      setWatchProvidersLength(
        () =>
          (response?.data?.result?.ads?.length ?? 0) +
          (response?.data?.result?.flatrate?.length ?? 0) +
          (response?.data?.result?.rent?.length ?? 0) +
          (response?.data?.result?.buy?.length ?? 0)
      );
    }
  };

  useEffect(() => {
    fetchWatchProviders();
  }, [movieId]);

  useEffect(() => {
    const updateConstraints = () => {
      setWindowWidth(window.innerWidth);
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => {
      window.removeEventListener("resize", updateConstraints);
    };
  }, []);

  const {
    data: movieDetails,
    isFetching: movieDetailsLoading,
    refetch: movieDetailsRefetch,
    isError: movieDetailsError,
  } = useGetMovieDetailsQuery(movieId);

  const {
    data: movieReviews,
    isFetching: movieReviewsLoading,
    refetch: movieReviewsRefetch,
    isError: movieReviewsError,
  } = useGetReviewsQuery({ media_id: movieId, media_type: "movie" });

  const controls = useAnimationControls();

  const [videoLeftConstraint, setVideoLeftConstraint] = useState<number>(0);
  const [videoIndex, setVideoIndex] = useState<number>(0);

  const updateLeft = () => {
    const maxIndex = movieDetails?.result?.videos?.results.length - 1;
    if (videoIndex < maxIndex) {
      setVideoIndex((prevValue) => prevValue + 1);
      setVideoLeftConstraint((prevValue) => prevValue + 330);
    }
  };

  const updateRight = () => {
    if (videoIndex > 0) {
      setVideoIndex((prevValue) => prevValue - 1);
      setVideoLeftConstraint((prevValue) => prevValue - 330);
    }
  };

  useEffect(() => {
    controls.start({ x: -videoLeftConstraint });
  }, [videoIndex, videoLeftConstraint]);

  const dispatch = useAppDispatch();

  const openReviewModal = () => {
    if (!user) {
      toast.info(
        // "Oops! Please signin or signup to leave a review and share your thoughts."
        "သည်းခံပါ သုံးသပ်ချက်ထားခဲ့ရန်နှင့် သင့်အတွေးအမြင်များကို မျှဝေရန် ကျေးဇူးပြု၍ ဝင်ရောက်ပါ သို့မဟုတ် စာရင်းသွင်းပါ။"
      );
      setTimeout(() => {
        dispatch(setAuthModal({ authModal: true, authModalType: "signin" }));
      }, 300);
    } else {
      dispatch(toggleReviewModal());
    }
  };

  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown2 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setStartPosition({ x: event.clientX, y: event.clientY });
    setIsDragging(false);
  };

  const handleMouseUp2 = (
    event: React.MouseEvent<HTMLButtonElement>,
    reviewId: number
  ) => {
    const distance = Math.sqrt(
      Math.pow(event.clientX - startPosition.x, 2) +
        Math.pow(event.clientY - startPosition.y, 2)
    );
    if (distance < 5 && !isDragging) {
      dispatch(openViewReviewModal({ reviewId: reviewId }));
    }
  };

  const handleMouseMove2 = () => {
    setIsDragging(true);
  };

  const { watchProvidersModal } = useAppSelector(
    (state) => state.watchProvidersModalSlice
  );

  const variants = {
    hidden: {
      y: window.innerHeight * 0.7,
      display: "none",
    },
    visible: {
      y: 0,
      display: "block",
    },
  };

  const controls2 = useAnimationControls();

  useEffect(() => {
    if (watchProvidersModal) {
      controls2.start("visible");
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [watchProvidersModal]);

  const closeModal = () => {
    controls2.start("hidden");
    dispatch(toggleWatchProvidersModal());
  };

  return (
    <>
      {movieDetailsLoading ? (
        <MediaDetailsLoading />
      ) : (
        <>
          <BackgroundMovieLayout
            windowWidth={windowWidth}
            backdropImage={
              !movieDetails?.result
                ? "error"
                : movieDetails?.result?.backdrop_path
            }
          >
            <AuthModal />
            <NavbarMobile2 />
            <Navbar activeLink={null} />

            <MediaDetails
              mediaType="movie"
              mediaDetails={movieDetails}
              windowWidth={windowWidth}
              watchProviders={watchProviders}
              watchProvidersLength={watchProvidersLength}
              wpLoading={wpLoading}
            />

            {/* YOUTUBE VIDEOS SECTION  */}

            <div className={classes.section_drag_cards}>
              <MediaSubTitle
                // title="Videos"
                title="ဗီဒီယိုများ"
                length={movieDetails?.result?.videos?.results?.length}
                type="videos"
                updateRight={updateRight}
                updateLeft={updateLeft}
              />
              <CardVideoPlayer
                controls={controls}
                mediaDetails={movieDetails}
                videoLeftConstraint={videoLeftConstraint}
              />
            </div>

            {/* MEDIA IMAGES SECTION  */}
            <div className={classes.section_drag_cards}>
              <MediaSubTitle
                length={movieDetails?.result?.images?.backdrops?.length}
                // title="Photos"
                title="ဓာတ်ပုံများ"
              />
              <MediaImages
                mediaDetails={movieDetails}
                windowWidth={windowWidth}
              />
            </div>

            {/* MEDIA CAST SECTION  */}
            <div className={classes.section_drag_cards}>
              <MediaSubTitle
                length={movieDetails?.result?.credits?.cast?.length}
                // title="Top Cast"
                title="ထိပ်တန်းသရုပ်ဆောင်"
              />

              <MediaCast
                mediaDetails={movieDetails}
                windowWidth={windowWidth}
              />
            </div>

            {/* MEDIA REVIEW SECTION  */}
            {movieReviewsLoading ? (
              <SkeletonCardsGrid />
            ) : (
              <div className={classes.section_drag_cards}>
                <div className={classes.section_cards}>
                  <MediaSubTitle
                    // title="User Reviews"
                    title="အသုံးပြုသူ သုံးသပ်ချက်များ"
                    length={movieReviews?.result.length}
                    type="review"
                    openReviewModal={openReviewModal}
                  />

                  <MediaReviews
                    mediaReviews={movieReviews}
                    windowWidth={windowWidth}
                    type="movie"
                    handleMouseDown={handleMouseDown2}
                    handleMouseMove={handleMouseMove2}
                    handleMouseUp={handleMouseUp2}
                  />
                </div>
              </div>
            )}

            {/* MEDIA RECOMMENDATIONS SECTION  */}
            <div className={classes.section_drag_cards}>
              <div className={classes.section_cards}>
                <MediaSubTitle
                  // title="Similar Movies"
                  title="ပုံစံတူရုပ်ရှင်များ"
                  length={movieDetails?.result?.recommendations?.results.length}
                />

                <MediaRecommendations
                  type="movies"
                  mediaDetails={movieDetails}
                  windowWidth={windowWidth}
                />
              </div>
            </div>
          </BackgroundMovieLayout>

          <ReviewModal
            windowWidth={windowWidth}
            mediaDetails={movieDetails}
            mediaReviews={movieReviews}
            mediaReviewsRefetch={movieReviewsRefetch}
            media_id={movieId}
            media_type="movie"
            media_name={
              movieDetails?.result?.title ?? movieDetails?.result?.name
            }
          />

          <ViewReviewModal
            windowWidth={windowWidth}
            mediaDetails={movieDetails}
            mediaReviews={movieReviews}
            media_type="movie"
          />

          <BottomNavbar />
          <SearchModal />
          <WatchlistModal />
          <UserRatingsModal />
          <ProfileModal />
          <Drawer />

          <motion.div
            className={classes.modal}
            variants={variants}
            initial="hidden"
            animate={controls2}
            transition={{
              duration: 0.1,
              type: "spring",
              //   damping: 10,
              damping: 20,
              stiffness: 100,
            }}
          >
            <div className={classes.modal_nav}>
              <button
                className={classes.close_btn}
                type="button"
                onClick={() => closeModal()}
              >
                <i className={`pi pi-times ${classes.close_icon}`} />
              </button>
            </div>

            <div className={classes.content}>
              {watchProviders?.ads && (
                <div className={classes.providers_wrapper}>
                  <p className={classes.bio_title}>Free To Watch</p>
                  <div className={classes.providers}>
                    {watchProviders?.ads?.map((dataItem: any, idx: number) => {
                      return (
                        <div className={classes.provider} key={idx}>
                          <div className={classes.image}>
                            <img
                              src={`https://media.themoviedb.org/t/p/original/${dataItem?.logo_path}`}
                              alt={dataItem?.provider_name}
                              className={classes.provider_img}
                            />
                          </div>
                          <p className={classes.bio}>
                            {dataItem?.provider_name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {watchProviders?.flatrate && (
                <div className={classes.providers_wrapper}>
                  <p className={classes.bio_title}>
                    {/* Streaming */}
                လွှ
                  </p>
                  <div className={classes.providers}>
                    {watchProviders?.flatrate?.map(
                      (dataItem: any, idx: number) => {
                        return (
                          <div className={classes.provider} key={idx}>
                            <div className={classes.image}>
                              <img
                                src={`https://media.themoviedb.org/t/p/original/${dataItem?.logo_path}`}
                                alt={dataItem?.provider_name}
                                className={classes.provider_img}
                              />
                            </div>
                            <p className={classes.bio}>
                              {dataItem?.provider_name}
                            </p>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}

              {watchProviders?.rent && (
                <div className={classes.providers_wrapper}>
                  <p className={classes.bio_title}>
                    {/* Rent */}
                    ငှား
                    </p>
                  <div className={classes.providers}>
                    {watchProviders?.rent?.map((dataItem: any, idx: number) => {
                      return (
                        <div className={classes.provider} key={idx}>
                          <div className={classes.image}>
                            <img
                              src={`https://media.themoviedb.org/t/p/original/${dataItem?.logo_path}`}
                              alt={dataItem?.provider_name}
                              className={classes.provider_img}
                            />
                          </div>
                          <p className={classes.bio}>
                            {dataItem?.provider_name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {watchProviders?.buy && (
                <div className={classes.providers_wrapper}>
                  <p className={classes.bio_title}>
                    {/* Buy */}
                    ဝယ်ပါ။
                    </p>
                  <div className={classes.providers}>
                    {watchProviders?.buy?.map((dataItem: any, idx: number) => {
                      return (
                        <div className={classes.provider} key={idx}>
                          <div className={classes.image}>
                            <img
                              src={`https://media.themoviedb.org/t/p/original/${dataItem?.logo_path}`}
                              alt={dataItem?.provider_name}
                              className={classes.provider_img}
                            />
                          </div>
                          <p className={classes.bio}>
                            {dataItem?.provider_name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </>
  );
};

export default MovieDetail;
